import React from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../../../components/ui';
import ConnectEmbeddedPanel from '../../connect/finance/connect-embedded-panel';
import COPY from '../../connect/finance/connect-finance-copy';
import { useFinanceSection } from '../section/finance-section-context';
import FinancePageShell from './finance-page-shell';

// One Stripe panel behind its own sub-menu entry. Mounting a Stripe component
// against an account Stripe has not finished checking shows an empty frame
// with no explanation, so the gate is here rather than inside each mount.
//
// There is no dedicated "not ready" string in connect-finance-copy.js —
// this reuses `copy.accountNotReady`'s before/linkLabel/after pieces around
// a real Link to Finance → Business Details, the page a restaurant actually
// finishes onboarding on, rather than adding a second copy of the same
// sentence under a new key. connect-finance.js (the admin's own Settlement
// screen) never renders this sentence at all — it has no restaurant branch
// any more, see that file's own header comment.
// `actions` sits in the page header, beside the title. Payouts uses it for
// the way back to what came before Stripe — offered whatever the account's
// state, because those records are about the company's own past rather than
// about Stripe, and are just as worth reaching while onboarding is unfinished.
//
// The Stripe component sits in the same thin-bordered card Payouts, Business
// Details and Order Reports draw (owner, 2026-09-23), rather than flush on
// the page: `bare` hands this card the panel's body, its wait and its
// failure line, and the Finance tokens carry dark mode.
const StripePanelPage = ({
  component, title, description, actions, column = 'reading',
}) => {
  const { status, instance, sessionFailure } = useFinanceSection();
  const copy = COPY.restaurant;
  const ready = !!status?.hasAccount && status?.connectStatus === 'READY';

  return (
    // Disputes asks for the full column: Stripe's disputes table needs the
    // width (QA 2026-09-25).
    <FinancePageShell column={column}>
      <PageHeader title={title} description={description} actions={actions} />
      {ready ? (
        <section className="overflow-hidden rounded-xl border border-fin-border bg-fin-surface">
          <div className="p-5">
            <ConnectEmbeddedPanel
              component={component}
              instance={instance}
              failure={sessionFailure ? copy.sessionFailed : ''}
              retryLabel={copy.retry}
              bare
            />
          </div>
        </section>
      ) : (
        <p className="text-sm text-slate-600 dark:text-slate-300">
          {copy.accountNotReady.before}
          <Link
            to="/dashboard/finance/business-details"
            className="font-medium text-blue-600 hover:underline dark:text-sky-400"
          >
            {copy.accountNotReady.linkLabel}
          </Link>
          {copy.accountNotReady.after}
        </p>
      )}
    </FinancePageShell>
  );
};

export default StripePanelPage;
